import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastProvider } from "@app/shared/src/components_v2";
import { ConsoleShell } from "./components/layouts/ConsoleShell";
import { FleetProvider } from "./components/providers/FleetProvider";
import { OrgProvider } from "./components/providers/OrgProvider";
import { SessionProvider } from "./components/providers/SessionProvider";
import { DeviceOverviewPage } from "./components/pages/DeviceOverviewPage";
import { DeviceSettingsPage } from "./components/pages/DeviceSettingsPage";
import { DevicesPage } from "./components/pages/DevicesPage";
import { EventReportPage } from "./components/pages/EventReportPage";
import { EventsPage } from "./components/pages/EventsPage";
import { HelpPage } from "./components/pages/HelpPage";
import { CreateAccountPage } from "./components/pages/CreateAccountPage";
import { ForgotPasswordPage } from "./components/pages/ForgotPasswordPage";
import { LoginPage } from "./components/pages/LoginPage";
import { MediaPage } from "./components/pages/MediaPage";
import { OnboardingPage } from "./components/pages/OnboardingPage";
import { OverviewPage } from "./components/pages/OverviewPage";
import { QrCodesPage } from "./components/pages/QrCodesPage";
import { SettingsPage } from "./components/pages/SettingsPage";
import { TeamPage } from "./components/pages/TeamPage";
import { TermsPage } from "./components/pages/TermsPage";

/**
 * Omnisight Fleet Commander — the safety-director side.
 *
 * Behind the same cookie session as the main dashboard. The driver side (the in-cab screen a QR
 * cab card opens, and the anonymous clip viewer) is its own app: ClientApps/wzcab. Links out to it
 * go through helpers/cabLinks.ts so a split deployment only has one knob to turn.
 */
export const App: React.FC = () => (
    <BrowserRouter>
        <SessionProvider>
            <OrgProvider>
                <FleetProvider>
                    <ToastProvider>
                        <Routes>
                            {/* Outside the shell: reachable signed out, or (welcome) before there is
                                an organization for the shell to show. */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/create-account" element={<CreateAccountPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/welcome" element={<OnboardingPage />} />
                            <Route element={<ConsoleShell />}>
                                <Route path="/overview" element={<OverviewPage />} />
                                <Route path="/events" element={<EventsPage />} />
                                <Route path="/events/:eventId" element={<EventReportPage />} />
                                <Route path="/media" element={<MediaPage />} />
                                <Route path="/devices" element={<DevicesPage />} />
                                <Route path="/devices/:deviceId" element={<DeviceOverviewPage />} />
                                <Route path="/devices/:deviceId/settings" element={<DeviceSettingsPage />} />
                                <Route path="/team" element={<TeamPage />} />
                                <Route path="/qr" element={<QrCodesPage />} />
                                <Route path="/settings" element={<SettingsPage />} />
                                <Route path="/help" element={<HelpPage />} />
                            </Route>

                            <Route path="/" element={<Navigate to="/overview" replace />} />
                            {/* Old bookmark of the placeholder era. */}
                            <Route path="/fleet" element={<Navigate to="/overview" replace />} />
                            <Route path="*" element={<Navigate to="/overview" replace />} />
                        </Routes>
                    </ToastProvider>
                </FleetProvider>
            </OrgProvider>
        </SessionProvider>
    </BrowserRouter>
);
